import type { ChangeEvent } from 'react';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Route, Switch, useHistory } from 'react-router-dom';
import {
    AppBar,
    Button,
    Container,
    CssBaseline,
    FormControl,
    Hidden,
    MenuItem,
    Select,
    Toolbar,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import classnames from 'classnames';

import { AuthRoutes } from '@klpkonsern/auth-core/routes';
import { useCurrentUser } from '@klp/auth';
import {
    useGetUserClientsQuery,
    useResetClientMutation,
} from '@klp/client/components/Select/rtkApi';
import type { AppDefinition } from '@klp/core/app/classes/AppDefinition';
import { AppRoutes } from '@klp/core/app/config';
import { appDispatch } from '@klp/core/app/store';
import { useAppParams } from '@klp/core/app/useAppParams';
import { ActionButton, Block, Typography } from '@klp/design';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Link } from '@klp/design/Link';
import { Logo } from '@klp/design/Logo';
import { useKlpStyles } from '@klp/design/Theme';
import { useLayoutContext } from '@klp/layout/context/LayoutContext';

const headerStyles = (theme: Theme) => ({
    appBarContainer: {
        height: theme.topbar.height,
    },
    appBar: {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.background.default,
        zIndex: theme.zIndex.appBar + 99,
        width: '100%',
        height: theme.topbar.height,
        boxShadow: `0 0 4px ${theme.palette.primary.light}`,
        [theme.breakpoints.up('sm')]: {
            paddingLeft: theme.menu.width,
        },
        transition: 'padding-left 0.25s linear',
    },
    appBarMenuPin: {
        paddingLeft: theme.menuOpen.width,
    },
    appBarToolbar: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    appBarToolbarLeft: {
        display: 'flex',
        alignItems: 'center',
        flexGrow: 1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        '& div:last-child': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    },
    appBarToolbarRight: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        '& > *': {
            marginLeft: '0.5em',
        },
    },
    logo: {
        height: '48px',
        fill: '#000',
        position: 'fixed',
        zIndex: 1150,
        top: '9px',
        left: '8px',
        transition: 'fill 0.25s linear, height 0.25s linear',
        width: '56px',
        '&:before': {
            display: 'none',
        },
    },
    appSwitch: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
        textTransform: 'none',
        fontSize: theme.klpVariant.small.fontSize,
        '&:hover': {
            backgroundColor: theme.palette.secondary.light,
        },
        '& .MuiInputBase-root': {
            fontSize: 'inherit',
            color: theme.palette.primary.main,
        },
        '& .MuiInputBase-input': {
            paddingTop: 4,
            paddingBottom: 5,
        },
        '& .MuiSelect-select:focus': {
            backgroundColor: 'transparent',
        },
    },
    appSelectorItem: {
        '&.Mui-selected': {
            backgroundColor: theme.palette.secondary.medium,
        },
        '&.MuiListItem-button:hover': {
            backgroundColor: theme.palette.primary.light,
        },
        '&.Mui-selected.MuiListItem-button:hover': {
            backgroundColor: theme.palette.secondary.light,
        },
    },
    clientSelectorButton: {
        textTransform: 'none',
        color: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.light,
        },
        '& .MuiButton-startIcon': {
            margin: 0,
        },
    },
    logout: {
        borderRadius: theme.spacing(0.2),
        '& .MuiButton-label span': {
            whiteSpace: 'nowrap',
        },
    },
    mobileMenuButton: {
        display: 'inline-block',
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
        },
        textTransform: 'uppercase',
        lineHeight: 0.5,
    },
});

interface IHeaderOwnProps {
    appSelection: Array<AppDefinition>;
    hideClientSelector: boolean;
    breadcrumbs: React.ComponentType;
}

export const Header: React.FC<IHeaderOwnProps> = ({
    appSelection = [],
    hideClientSelector = false,
    breadcrumbs: CustomBreadcrumbs,
}) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, headerStyles);
    const params = useAppParams();
    const history = useHistory();
    const dispatch = useDispatch();

    const { currentUser: user, isAuthenticated } = useCurrentUser();
    const { data: clients = [] } = useGetUserClientsQuery(undefined, {
        skip: !isAuthenticated || !user,
    });

    // Layout context
    const { menuPin, toggleMenu } = useLayoutContext();

    // Dispatch actions
    const { dispatchTriggerChange } = appDispatch(dispatch);
    const [resetClient] = useResetClientMutation();

    const handleClickMobileMenu = () => {
        toggleMenu();
    };

    const handleAppSelect = (event: ChangeEvent<HTMLInputElement>) => {
        const entry: AppDefinition = appSelection[event.target.value];
        if (entry.openInNewTab) {
            window.open(entry.url, '_blank');
        } else {
            window.location.href = entry.url;
        }
    };

    const handleClientSelect = (event: ChangeEvent<{ value: string }>) => {
        const selected = clients.find(
            (kunde) => kunde.kundeNummer === event.target.value,
        );
        if (!selected) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        params.selectedClient = (selected as any).toClient();
        if (history.location.pathname === AppRoutes.mainPage()) {
            dispatchTriggerChange();
        } else {
            history.push(AppRoutes.mainPage());
        }
    };

    const handleClientReset = () => {
        resetClient();
        dispatchTriggerChange();
    };

    const { selectedClient, isAdmin } = params;

    return (
        <div className={classnames('Header', classes.appBarContainer)}>
            <CssBaseline />
            <AppBar
                className={classnames(classes.appBar, {
                    [classes.appBarMenuPin]: menuPin,
                })}
                position="fixed"
            >
                <Container maxWidth="lg">
                    <Toolbar
                        className={classes.appBarToolbar}
                        disableGutters={true}
                    >
                        {user && (
                            <div className={classes.appBarToolbarLeft}>
                                <Hidden mdUp={true}>
                                    <Link
                                        className={classes.logo}
                                        href={AppRoutes.mainPage()}
                                    >
                                        <Logo />
                                    </Link>
                                </Hidden>
                                <Hidden smDown={true}>
                                    <CustomBreadcrumbs />
                                </Hidden>
                            </div>
                        )}

                        {!user && (
                            <div className={classes.appBarToolbarLeft}>
                                <Switch>
                                    <Route path={AuthRoutes.login} exact={true}>
                                        {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
                                        <Fragment />{' '}
                                    </Route>
                                    <Route>
                                        <Link
                                            className={classes.logo}
                                            href={AppRoutes.mainPage()}
                                        >
                                            <Logo />
                                        </Link>
                                    </Route>
                                </Switch>
                            </div>
                        )}

                        <div className={classes.appBarToolbarRight}>
                            {!hideClientSelector && (
                                <Hidden xsDown={true}>
                                    {isAdmin && selectedClient && (
                                        <ActionButton
                                            actionType="cancel"
                                            onClick={handleClientReset}
                                            translation={
                                                selectedClient.kundeName
                                            }
                                        />
                                    )}

                                    {!isAdmin && selectedClient && (
                                        <>
                                            {clients.length === 1 && (
                                                <Block
                                                    klpTypographyVariant="small"
                                                    translation={
                                                        selectedClient.kundeName
                                                    }
                                                />
                                            )}
                                            {clients.length > 1 &&
                                                clients.length < 10 && (
                                                    <FormControl>
                                                        <Select
                                                            value={
                                                                selectedClient.kundeNummer
                                                            }
                                                            onChange={
                                                                handleClientSelect
                                                            }
                                                            color="secondary"
                                                        >
                                                            {clients.map(
                                                                ({
                                                                    kundeNummer,
                                                                    kundeName,
                                                                }) => (
                                                                    <MenuItem
                                                                        key={
                                                                            kundeNummer
                                                                        }
                                                                        value={
                                                                            kundeNummer
                                                                        }
                                                                    >
                                                                        {
                                                                            kundeName
                                                                        }
                                                                    </MenuItem>
                                                                ),
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                )}
                                            {clients.length >= 10 && (
                                                <Button
                                                    startIcon={
                                                        <Illustration
                                                            icon={
                                                                IconName.CHANGE
                                                            }
                                                            klpColor="main"
                                                            marginHorizontal="tiny"
                                                        />
                                                    }
                                                    className={
                                                        classes.clientSelectorButton
                                                    }
                                                >
                                                    {selectedClient.kundeName}
                                                </Button>
                                            )}
                                        </>
                                    )}
                                </Hidden>
                            )}

                            {appSelection.length > 1 && isAdmin && (
                                <Hidden smDown={true}>
                                    <Block
                                        className={`MuiButton-root MuiButton-text ${classes.appSwitch}`}
                                    >
                                        <Illustration
                                            icon={IconName.CHANGE}
                                            klpColor="main"
                                            marginHorizontal="tiny"
                                        />
                                        <Select
                                            value={0}
                                            onChange={handleAppSelect}
                                            disableUnderline={true}
                                        >
                                            {appSelection.map(
                                                ({ name }, index) => (
                                                    <MenuItem
                                                        key={index}
                                                        className={
                                                            classes.appSelectorItem
                                                        }
                                                        value={index}
                                                    >
                                                        {name}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </Select>
                                    </Block>
                                </Hidden>
                            )}

                            {user && (
                                <Hidden xsDown={true}>
                                    <Button
                                        href={AuthRoutes.logout}
                                        startIcon={
                                            <Illustration
                                                icon={IconName.LOGOUT}
                                                klpColor="main"
                                            />
                                        }
                                        variant="outlined"
                                        className={classes.logout}
                                    >
                                        {t('auth.logout')}
                                    </Button>
                                </Hidden>
                            )}

                            <Switch>
                                {/* eslint-disable react/jsx-no-useless-fragment */}
                                <Route path={AuthRoutes.login} exact={true}>
                                    <Fragment />
                                </Route>
                                {/* eslint-enable react/jsx-no-useless-fragment */}
                                <Route>
                                    {!user && (
                                        <Hidden xsDown={true}>
                                            <Button
                                                href={AuthRoutes.login}
                                                startIcon={
                                                    <Illustration
                                                        icon={
                                                            IconName.LOGIN_REQUIRED
                                                        }
                                                        klpColor="main"
                                                    />
                                                }
                                                variant="outlined"
                                                className={classes.logout}
                                            >
                                                {t('auth.login')}
                                            </Button>
                                        </Hidden>
                                    )}
                                </Route>
                            </Switch>

                            <Hidden smUp={true}>
                                <Button
                                    onClick={handleClickMobileMenu}
                                    className={classes.mobileMenuButton}
                                >
                                    <Illustration
                                        icon={IconName.MENU}
                                        klpColor="main"
                                    />
                                    <Typography
                                        translation="general.menu"
                                        klpTypographyVariant="small"
                                    />
                                </Button>
                            </Hidden>
                        </div>
                    </Toolbar>
                </Container>
            </AppBar>
        </div>
    );
};

Header.displayName = 'Header';
